import { A } from '@solidjs/router';
import { For, Show, createResource, createSignal } from 'solid-js';
import { api, Customer, Partner } from '../api';
import { ErrorLine, PageHeader, StatusPill, useAction } from '../ui';

const tiers = [
  { v: 'small', l: 'Small · up to 3 zones' },
  { v: 'medium', l: 'Medium · up to 10 zones' },
  { v: 'large', l: 'Large · up to 20 zones' },
  { v: 'custom', l: 'Custom' },
];

export default function Customers() {
  const [customers, { refetch }] = createResource(() => api<Customer[]>('/customers'));
  const [partners] = createResource(() => api<Partner[]>('/partners'));
  const [form, setForm] = createSignal({ name: '', tier: 'small', partner_id: '', zone_limit: 25, trial_days: 0 });
  const a = useAction();
  const partnerName = (id: string) => partners()?.find((p) => p.id === id)?.name ?? '';
  const pid = () => form().partner_id || partners()?.[0]?.id || '';

  const create = (e: Event) => {
    e.preventDefault();
    a.run(async () => {
      const f = form();
      await api(`/partners/${pid()}/customers`, { method: 'POST',
        json: { name: f.name, tier: f.tier, zone_limit: f.tier === 'custom' ? f.zone_limit : undefined,
          ...(f.trial_days ? { status: 'trial', trial_ends_at: new Date(Date.now() + f.trial_days * 864e5).toISOString() } : {}) } });
      setForm({ ...f, name: '' });
      refetch();
    });
  };

  return (
    <>
      <PageHeader title="Customers" />
      <Show when={(partners() ?? []).length > 0}>
        <form class="card-z mb-6 flex flex-wrap items-end gap-3" onSubmit={create}>
          <label class="form-control"><span class="label-text font-semibold">Customer name</span>
            <input class="input input-bordered" required value={form().name} onInput={(e) => setForm({ ...form(), name: e.currentTarget.value })} /></label>
          <Show when={(partners() ?? []).length > 1}>
            <label class="form-control"><span class="label-text font-semibold">Partner</span>
              <select class="select select-bordered" value={pid()} onChange={(e) => setForm({ ...form(), partner_id: e.currentTarget.value })}>
                <For each={partners()}>{(p) => <option value={p.id}>{p.name}</option>}</For>
              </select></label>
          </Show>
          <label class="form-control"><span class="label-text font-semibold">Tier</span>
            <select class="select select-bordered" value={form().tier} onChange={(e) => setForm({ ...form(), tier: e.currentTarget.value })}>
              <For each={tiers}>{(t) => <option value={t.v}>{t.l}</option>}</For>
            </select></label>
          <Show when={form().tier === 'custom'}>
            <label class="form-control w-28"><span class="label-text font-semibold">Zones</span>
              <input type="number" min="1" class="input input-bordered" value={form().zone_limit}
                onInput={(e) => setForm({ ...form(), zone_limit: Number(e.currentTarget.value) })} /></label>
          </Show>
          <label class="form-control"><span class="label-text font-semibold">Start</span>
            <select class="select select-bordered" value={form().trial_days} onChange={(e) => setForm({ ...form(), trial_days: Number(e.currentTarget.value) })}>
              <option value="0">Billed from today</option><option value="14">14-day trial</option><option value="30">30-day trial</option>
            </select></label>
          <button class="btn btn-primary" disabled={a.busy()}>New customer</button>
          <ErrorLine error={a.error} />
        </form>
      </Show>
      <div class="card-z overflow-x-auto p-0">
        <table class="table">
          <thead><tr><th class="th">Customer</th><th class="th">Partner</th><th class="th">Zones</th><th class="th">Tier</th><th class="th">Status</th></tr></thead>
          <tbody>
            <For each={customers()} fallback={<tr><td colspan="5" class="text-ink-3">No customers yet.</td></tr>}>
              {(c) => (
                <tr class="hover">
                  <td><A href={`/customers/${c.id}`} class="font-semibold hover:text-magenta">{c.name}</A></td>
                  <td class="text-ink-2">{partnerName(c.partner_id)}</td>
                  <td class="mono">{c.zone_count} / {c.zone_limit}</td>
                  <td class="capitalize text-ink-2">{c.tier}</td>
                  <td><StatusPill tone={c.status === 'active' || c.status === 'trial' ? 'ok' : 'off'}>{c.status}</StatusPill></td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </>
  );
}
